import { COLS, MAX_DEPTH, MAP_W, MAP_H, cellOf, colOf, inBounds, xOf, yOf } from '../constants';
import { FLAG_BUILDING, FLAG_POWER, FLAG_RAIL, ORE_NONE, type OreType, type Terrain } from '../types';

export function createTerrain(): Terrain {
  return {
    depth: new Uint8Array(COLS),
    oreType: new Uint8Array(COLS * MAX_DEPTH),
    oreAmount: new Uint8Array(COLS * MAX_DEPTH),
    flags: new Uint8Array(COLS),
    reclaimed: new Uint8Array(COLS),
    version: 1,
  };
}

export function depthAt(terrain: Terrain, x: number, y: number): number {
  return terrain.depth[colOf(x, y)] ?? 0;
}

/** Peeps can traverse between 4-neighbors differing by at most one level. */
export function walkableEdge(terrain: Terrain, a: number, b: number): boolean {
  const da = terrain.depth[a] ?? 0;
  const db = terrain.depth[b] ?? 0;
  return Math.abs(da - db) <= 1;
}

export function hasFlag(terrain: Terrain, col: number, flag: number): boolean {
  return ((terrain.flags[col] ?? 0) & flag) !== 0;
}

export function setFlag(terrain: Terrain, col: number, flag: number): void {
  terrain.flags[col] = (terrain.flags[col] ?? 0) | flag;
  terrain.version += 1;
}

export function clearFlag(terrain: Terrain, col: number, flag: number): void {
  terrain.flags[col] = (terrain.flags[col] ?? 0) & ~flag;
  terrain.version += 1;
}

export interface DugOre {
  type: OreType;
  amountDt: number;
}

/**
 * Remove one block from a column: the cell at z = current depth. Returns any
 * ore that block contained. Caller is responsible for legality checks.
 */
export function digColumn(terrain: Terrain, col: number): DugOre {
  const z = terrain.depth[col] ?? 0;
  if (z >= MAX_DEPTH) return { type: ORE_NONE, amountDt: 0 };
  const cell = cellOf(col, z);
  const type = (terrain.oreType[cell] ?? ORE_NONE) as OreType;
  const amountDt = terrain.oreAmount[cell] ?? 0;
  terrain.oreType[cell] = ORE_NONE;
  terrain.oreAmount[cell] = 0;
  terrain.depth[col] = z + 1;
  // Breaking ground again un-heals the land.
  terrain.reclaimed[col] = 0;
  terrain.version += 1;
  return { type, amountDt };
}

/**
 * Fill an excavated column back to grade. Already-dug cells stay empty (the
 * fill is barren spoil), but undug ore below keeps counting as reserves.
 * Returns false on virgin ground. Caller checks buildings/piles/cost.
 */
export function reclaimColumn(terrain: Terrain, col: number): boolean {
  if ((terrain.depth[col] ?? 0) === 0) return false;
  terrain.depth[col] = 0;
  terrain.reclaimed[col] = 1;
  terrain.version += 1;
  return true;
}

export const NEIGHBOR_OFFSETS: readonly (readonly [number, number])[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

export function forEachNeighbor(col: number, callback: (neighbor: number) => void): void {
  const x = xOf(col);
  const y = yOf(col);
  for (const [dx, dy] of NEIGHBOR_OFFSETS) {
    const nx = x + dx;
    const ny = y + dy;
    if (inBounds(nx, ny)) callback(colOf(nx, ny));
  }
}

/**
 * A dig order on `col` is executable iff some 4-neighbor is within one level
 * of the column's current floor and is not blocked by a building. Miners stand
 * on that neighbor and dig the face — they never stand on the tile they dig,
 * so they cannot strand themselves.
 */
export function digWorkTiles(terrain: Terrain, col: number, out: number[]): number[] {
  out.length = 0;
  const depth = terrain.depth[col] ?? 0;
  forEachNeighbor(col, (neighbor) => {
    if (hasFlag(terrain, neighbor, FLAG_BUILDING)) return;
    const neighborDepth = terrain.depth[neighbor] ?? 0;
    if (Math.abs(neighborDepth - depth) <= 1) out.push(neighbor);
  });
  return out;
}

export function isDiggable(terrain: Terrain, col: number): boolean {
  const depth = terrain.depth[col] ?? 0;
  if (depth >= MAX_DEPTH) return false;
  if (hasFlag(terrain, col, FLAG_BUILDING)) return false;
  if (hasFlag(terrain, col, FLAG_RAIL)) return false; // nobody digs up the line
  if (hasFlag(terrain, col, FLAG_POWER)) return false; // nor under the pylons
  return true;
}

export { MAP_W, MAP_H };
